// The pages after the first review's last answer, one after the other (their order and why:
// getPagesAfterTheFirstReview in firstExperienceGelRules.ts): T2b's voice check where there is one, today's streak
// day, the exam question, and the checkpoint with its upsell.
//
// HelperMethodsHook opens the first of them on the press after the last answer; each page's own way on (the voice
// check closing, the streak page's "continue", the exam question's two buttons) opens the next. The checkpoint's
// "continue" ends the first review as before (handleNavigationForMiniReviewCompletion), asking the exam question
// there only where it was not asked already (a learner whose sequence did not run). The ask for notification
// permission (notification-permission-asks.ts) stays after the checkpoint: an exam date saved before the checkpoint
// is kept here and handed to that ask (takeTheExamSavedBeforeTheFirstReviewsCheckpoint), so the ask neither covers
// the checkpoint's upsell nor loses the exam it leads with.

import { create } from "zustand";
import {
  cardStore,
  navigationWithStatesStore,
  useReviewDeckStore,
  useStreakStore,
  userStore,
} from "../../../zustand/globalStore";
import { UserType } from "../../../constants/GlobalTypesAndEnumsAndConstants";
import {
  getPagesAfterTheFirstReview,
  type PageAfterTheFirstReview,
} from "../../../first-experience-tests/firstExperienceGelRules";
import { recordTheFirstReviewAsTodaysStreakDay } from "../../../backend/models/StreakModel";
import { trackError } from "../../../utils/analytics/track-error";
import { isTheFirstReviewOnScreenTheAccountsFirstReview } from "./answer-first/answerFirstReview";
import type { SavedExamForNotificationCopy } from "../../../backend/utils/notification-permission-asks";

// On a computer the pages of the sequence start at the same height, so going from one to the next does not jump
// (owner, 2026-10-09: "make sure the matching pages around it start at the same positioning"): the streak page's
// count, the exam question's heading, the first upsell's card and the checkpoint's "great work" card. The streak
// page and the exam question fill the page from its top; the upsell and the checkpoint sit in the review's
// column, which starts below the review's own header (its X), REVIEW_COLUMN_TOP_ON_A_COMPUTER_IN_PX down.
export const TOP_OF_THE_PAGES_AFTER_THE_FIRST_REVIEW_ON_A_COMPUTER_IN_PX = 112;
const REVIEW_COLUMN_TOP_ON_A_COMPUTER_IN_PX = 108;
/** The room above the first upsell's card and the checkpoint's "great work" card, in the review's column */
export const ROOM_ABOVE_A_CARD_IN_THE_REVIEW_COLUMN_AFTER_THE_FIRST_REVIEW_IN_PX =
  TOP_OF_THE_PAGES_AFTER_THE_FIRST_REVIEW_ON_A_COMPUTER_IN_PX - REVIEW_COLUMN_TOP_ON_A_COMPUTER_IN_PX;

// The streak page waits this long at most for the Worker to record the first review's streak day, so it never shows
// an old count; without an answer by then the page is left out
const STREAK_DAY_RECORD_WAIT_MILLISECONDS = 4_000;

type PagesAfterTheFirstReviewState = {
  /** The set whose first review these pages follow; another set starts afresh */
  deckId: string | null;
  pagesShown: PageAfterTheFirstReview[];
  /** True while the streak page on screen is the first review's (its "continue" opens the next page) */
  isTheStreakPageOnScreenTheFirstReviews: boolean;
  /** True while the first review's streak day is being written, before its page opens */
  isRecordingTheStreakDay: boolean;
  /** The exam date saved on the exam question before the checkpoint, for the ask after the checkpoint */
  examSavedBeforeTheCheckpoint: SavedExamForNotificationCopy | null;
};

export const usePagesAfterTheFirstReviewStore = create<PagesAfterTheFirstReviewState>(() => ({
  deckId: null,
  pagesShown: [],
  isTheStreakPageOnScreenTheFirstReviews: false,
  isRecordingTheStreakDay: false,
  examSavedBeforeTheCheckpoint: null,
}));

function asksForTheExamDate(): boolean {
  const userType = userStore.getState().currentUser?.user_type;
  return userType === UserType.Student || userType === UserType.TeacherOrProfessor;
}

// The voice check, where there is one, is opened by its own caller before these: it is left out here
function getThePagesInOrder(): PageAfterTheFirstReview[] {
  return getPagesAfterTheFirstReview({ endsWithVoiceCheck: false, asksForTheExamDate: asksForTheExamDate() });
}

function getTheStateForThisSet(): PagesAfterTheFirstReviewState {
  const deckId = useReviewDeckStore.getState().currentReviewDeck?.id ?? null;
  const state = usePagesAfterTheFirstReviewStore.getState();
  if (state.deckId === deckId) return state;
  const freshState: PagesAfterTheFirstReviewState = {
    deckId,
    pagesShown: [],
    isTheStreakPageOnScreenTheFirstReviews: false,
    isRecordingTheStreakDay: false,
    examSavedBeforeTheCheckpoint: null,
  };
  usePagesAfterTheFirstReviewStore.setState(freshState);
  return freshState;
}

function noteShown(page: PageAfterTheFirstReview) {
  const { pagesShown } = usePagesAfterTheFirstReviewStore.getState();
  if (!pagesShown.includes(page)) usePagesAfterTheFirstReviewStore.setState({ pagesShown: [...pagesShown, page] });
}

// Where the learner is: the account, the set under review and the page
function getWhereTheLearnerIs() {
  return {
    userId: userStore.getState().currentUser?.id ?? null,
    deckId: useReviewDeckStore.getState().currentReviewDeck?.id ?? null,
    pathname: window.location.pathname,
  };
}

async function openTheStreakPage() {
  const whereTheLearnerWas = getWhereTheLearnerIs();
  const { userId } = whereTheLearnerWas;
  usePagesAfterTheFirstReviewStore.setState({ isRecordingTheStreakDay: true });
  const outcome = await Promise.race([
    recordTheFirstReviewAsTodaysStreakDay(userId),
    new Promise<"timed_out">((resolve) => setTimeout(() => resolve("timed_out"), STREAK_DAY_RECORD_WAIT_MILLISECONDS)),
  ]);
  usePagesAfterTheFirstReviewStore.setState({ isRecordingTheStreakDay: false });
  if (outcome === "timed_out") {
    // The request's own failures are reported where it is made (recordTheFirstReviewAsTodaysStreakDay)
    trackError(new Error("record_first_review_day did not answer within the streak page's wait"), {
      scope: "first-review-streak-day",
      userId,
      level: "warning",
      extra: { waitMilliseconds: STREAK_DAY_RECORD_WAIT_MILLISECONDS },
    });
  }
  // The learner left the review meanwhile (its X, the browser's back, another account): what follows the first
  // review opens on no other page or review, where a streak page or a checkpoint would make no sense
  const whereTheLearnerIsNow = getWhereTheLearnerIs();
  const isTheLearnerStillOnThatReview =
    whereTheLearnerIsNow.userId === whereTheLearnerWas.userId &&
    whereTheLearnerIsNow.deckId === whereTheLearnerWas.deckId &&
    whereTheLearnerIsNow.pathname === whereTheLearnerWas.pathname;
  if (!isTheLearnerStillOnThatReview) return;
  // Without a day the page would celebrate a streak the learner does not have (an older Worker's 404, a network
  // error, or no answer within the wait): the sequence goes on to its next page instead
  if (outcome !== "recorded" && outcome !== "already_today") {
    openTheNextPageAfterTheFirstReview();
    return;
  }
  usePagesAfterTheFirstReviewStore.setState({ isTheStreakPageOnScreenTheFirstReviews: true });
  useStreakStore.getState().setShouldShowStreakIncreasedPage(true);
}

/**
 * Opens the next page after the first review's last answer (after the voice check, which its own caller opens).
 * Returns false when none is left before the checkpoint's "continue".
 */
export function openTheNextPageAfterTheFirstReview(): boolean {
  const state = getTheStateForThisSet();
  if (state.isRecordingTheStreakDay) return true;
  // Only for the account's own first review (not a first review an older account took over by logging in on the
  // sign-up sheet: isTheFirstReviewOnScreenTheAccountsFirstReview)
  const isTheAccountsFirstReview = isTheFirstReviewOnScreenTheAccountsFirstReview(state.deckId);
  for (const page of getThePagesInOrder()) {
    if (state.pagesShown.includes(page)) continue;
    noteShown(page);
    if (page === "streak") {
      if (!isTheAccountsFirstReview || !userStore.getState().currentUser?.id) continue;
      void openTheStreakPage();
      return true;
    }
    if (page === "exam_date") {
      if (!isTheAccountsFirstReview) continue;
      navigationWithStatesStore.getState().setShowAddYourExamAfterMiniReviewPage(true);
      return true;
    }
    if (page === "checkpoint") {
      cardStore.getState().setShowCheckpointWithPerformanceOverviewPage(true);
      return true;
    }
  }
  return false;
}

/** The streak page's "continue" (or arrow key): true when it was the first review's, and the next page opened */
export function continueFromTheFirstReviewsStreakPage(): boolean {
  if (!usePagesAfterTheFirstReviewStore.getState().isTheStreakPageOnScreenTheFirstReviews) return false;
  usePagesAfterTheFirstReviewStore.setState({ isTheStreakPageOnScreenTheFirstReviews: false });
  return openTheNextPageAfterTheFirstReview();
}

/**
 * The exam question's two ways on: true when it was asked before the checkpoint, and the checkpoint opened. The
 * exam saved there (none for "there is no exam") is kept for the ask for notifications after the checkpoint.
 */
export function continueFromTheFirstReviewsExamQuestion(savedExam?: SavedExamForNotificationCopy): boolean {
  const { pagesShown } = usePagesAfterTheFirstReviewStore.getState();
  if (!pagesShown.includes("exam_date") || pagesShown.includes("checkpoint")) return false;
  usePagesAfterTheFirstReviewStore.setState({ examSavedBeforeTheCheckpoint: savedExam ?? null });
  return openTheNextPageAfterTheFirstReview();
}

/**
 * At the checkpoint's "continue": the exam date saved on the exam question before the checkpoint, once, for the ask
 * for notifications, which otherwise comes right after the exam question. Null when none was.
 */
export function takeTheExamSavedBeforeTheFirstReviewsCheckpoint(): SavedExamForNotificationCopy | null {
  const { examSavedBeforeTheCheckpoint, deckId } = usePagesAfterTheFirstReviewStore.getState();
  const currentDeckId = useReviewDeckStore.getState().currentReviewDeck?.id ?? null;
  usePagesAfterTheFirstReviewStore.setState({ examSavedBeforeTheCheckpoint: null });
  return deckId === currentDeckId ? examSavedBeforeTheCheckpoint : null;
}

/** True while a page of the sequence is on screen or opening, so the review's own navigation stays put */
export function isAPageAfterTheFirstReviewOnScreen(): boolean {
  const state = usePagesAfterTheFirstReviewStore.getState();
  return state.isRecordingTheStreakDay || state.isTheStreakPageOnScreenTheFirstReviews;
}

/**
 * At the checkpoint's "continue": whether the exam question is still to ask (it was not asked before the checkpoint,
 * because the sequence did not run: the exam question is then asked there, as before 2026-10-19)
 */
export function isTheExamQuestionStillToAskAfterTheCheckpoint(): boolean {
  return !usePagesAfterTheFirstReviewStore.getState().pagesShown.includes("exam_date");
}
